import { runtimeTenantConfig, userSession } from 'state/session';
import { getStorageItem, removeStorageItem, setStorageItem } from 'utils/ui/storage';

/**
 * Per-tab record of a session that was handed to this browsing context by another
 * (e.g. a DCC tab that received a session via the `e3d-sid` URL parameter).
 *
 * Source of truth is an in-memory module variable; sessionStorage is a best-effort
 * mirror for reload survival — the same pattern used by `voipTabId.ts`.
 *
 * The storage helpers can throw (e.g. when sessionStorage is blocked). Those throws
 * are contained here so a storage-blocked browser degrades to in-memory-only behaviour
 * instead of rejecting E3DRuntime.init().
 */

const KEY = 'e3d-inherited-sid';

let inheritedSessionId: string | undefined;

// Restore from sessionStorage on module initialisation (survives a hard reload).
try {
  const stored = getStorageItem('sessionStorage', KEY);
  if (typeof stored === 'string' && stored) {
    inheritedSessionId = stored;
  }
} catch {
  // sessionStorage blocked — in-memory only, acceptable degradation.
}

/**
 * Record that this browsing context inherited `sessionId` from another context.
 * Must be called synchronously, before any `await`, at the hand-off entry point so
 * that the fact is available before the first render cycle.
 *
 * Falsy / empty writes are no-ops.
 */
export function markSessionInherited(sessionId: string | undefined): void {
  if (!sessionId) return;
  inheritedSessionId = sessionId;
  try {
    setStorageItem('sessionStorage', KEY, sessionId);
  } catch {
    // sessionStorage blocked — in-memory record is sufficient.
  }
}

export function getInheritedSessionId(): string | undefined {
  return inheritedSessionId;
}

/**
 * This check is not 100% reliable since the tenant attribute of the session can technically change during a session.
 * However, if the tenant that created the session is different from the tenant that is currently running, the session was inherited.
 * We use this check as a safety net for cases where the e3d-sid parameter was not present in the URL at the time of web component initialization.
 * @returns true if the session was created with a different tenant, false otherwise.
 */

export function wasSessionCreatedWithDifferentTenant(): boolean {
  const sessionPartnerToken = userSession().get().partnerToken;
  const myPartnerToken = runtimeTenantConfig().partnerToken;
  return !!(sessionPartnerToken && myPartnerToken && sessionPartnerToken !== myPartnerToken);
}

/**
 * Returns true when this browsing context inherited a session it did not create.
 *
 * When a hand-off IS on record but `sessionId` is falsy (load still in flight),
 * answers pessimistically — treats the context as inherited — to prevent a race
 * where the component mounts before the server session resolves.
 *
 * The check for the tenant that created the session does not apply for scheduled
 * sessions, as scheduled sessions can have different entrypoints and tenants
 * can change over time (since scheduled session are created in advance).
 */
export function isInheritedSession(sessionId?: string): boolean {
  if (!inheritedSessionId) return false;
  if (!sessionId) return true; // load in flight — pessimistic
  return (
    inheritedSessionId === sessionId ||
    (wasSessionCreatedWithDifferentTenant() && !userSession().get('scheduledStartTime'))
  );
}

/**
 * Clear the inherited-session record. Call when this context creates its own
 * session so it can become the host.
 */
export function clearInheritedSession(): void {
  inheritedSessionId = undefined;
  try {
    removeStorageItem('sessionStorage', KEY);
  } catch {
    // sessionStorage blocked — in-memory clear is sufficient.
  }
}
